(() => {
  if (customElements.get('moretti-cart-collection-carousel')) return;

  class MorettiCartCollectionCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const drawer = this.closest('cart-drawer');
      if (drawer && !drawer.classList.contains('active') && typeof MutationObserver === 'function') {
        if (!this.drawerOpenObserver) {
          this.drawerOpenObserver = new MutationObserver(() => {
            if (this.isConnected && drawer.classList.contains('active')) this.connectedCallback();
          });
          this.drawerOpenObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }
        return;
      }
      this.drawerOpenObserver?.disconnect();
      this.drawerOpenObserver = null;
      this.initialized = true;

      this.viewport = this.querySelector('[data-moretti-collection-viewport]');
      this.track = this.querySelector('[data-moretti-collection-track]');
      this.originalSlides = Array.from(this.querySelectorAll('[data-moretti-collection-slide]'));
      this.previousButton = this.querySelector('[data-moretti-collection-prev]');
      this.nextButton = this.querySelector('[data-moretti-collection-next]');
      this.status = this.querySelector('[data-moretti-collection-status]');
      this.interval = Math.max(4000, Number(this.dataset.autoplay) || 4500);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.position = 1;
      this.logicalIndex = 0;
      this.timer = null;
      this.pointerStartX = null;
      this.suppressClick = false;
      this.inView = true;
      this.isHovered = false;
      this.isFocused = false;
      this.isInteracting = false;

      if (!this.viewport || !this.track || this.originalSlides.length < 2) {
        this.previousButton?.setAttribute('hidden', '');
        this.nextButton?.setAttribute('hidden', '');
        return;
      }

      this.appendLoopClones();
      this.bindEvents();
      this.jumpTo(this.position);
      this.updateAccessibility();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.drawerOpenObserver?.disconnect();
      this.drawerOpenObserver = null;
      this.stopAutoplay();
      window.clearTimeout(this.resumeTimer);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    appendLoopClones() {
      const firstClone = this.originalSlides[0].cloneNode(true);
      const lastClone = this.originalSlides[this.originalSlides.length - 1].cloneNode(true);

      firstClone.dataset.morettiCollectionClone = 'true';
      lastClone.dataset.morettiCollectionClone = 'true';
      firstClone.removeAttribute('data-moretti-collection-slide');
      lastClone.removeAttribute('data-moretti-collection-slide');
      this.disableSlide(firstClone);
      this.disableSlide(lastClone);
      this.track.prepend(lastClone);
      this.track.append(firstClone);
    }

    bindEvents() {
      this.previousButton.addEventListener('click', () => {
        this.pauseAfterInteraction();
        this.move(-1, true);
      });
      this.nextButton.addEventListener('click', () => {
        this.pauseAfterInteraction();
        this.move(1, true);
      });

      this.addEventListener('pointerenter', () => {
        this.isHovered = true;
        this.stopAutoplay();
      });
      this.addEventListener('pointerleave', () => {
        this.isHovered = false;
        this.startAutoplay();
      });
      this.addEventListener('focusin', () => {
        this.isFocused = true;
        this.stopAutoplay();
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.isFocused = false;
          this.startAutoplay();
        }
      });

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('pointercancel', () => this.onPointerCancel());
      this.viewport.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        },
        true
      );

      this.track.addEventListener('transitionend', (event) => {
        if (event.propertyName !== 'transform') return;
        this.normalizeLoopPosition();
      });

      this.handleVisibilityChange = () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      };
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            if (this.inView) this.startAutoplay();
            else this.stopAutoplay();
          },
          { threshold: 0.35 }
        );
        this.observer.observe(this);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.pointerStartX = event.clientX;
      this.isInteracting = true;
      this.stopAutoplay();
    }

    onPointerUp(event) {
      if (this.pointerStartX === null) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      this.isInteracting = false;

      if (Math.abs(distance) >= 40) {
        this.suppressClick = true;
        this.move(distance < 0 ? 1 : -1, true);
      }

      this.pauseAfterInteraction();
    }

    onPointerCancel() {
      this.pointerStartX = null;
      this.isInteracting = false;
      this.pauseAfterInteraction();
    }

    move(direction, announce = false) {
      this.position += direction;
      this.logicalIndex = (this.logicalIndex + direction + this.originalSlides.length) % this.originalSlides.length;
      this.track.classList.remove('is-jumping');
      this.track.style.transform = `translate3d(-${this.position * 100}%, 0, 0)`;
      this.updateAccessibility(announce);
    }

    normalizeLoopPosition() {
      const lastPosition = this.originalSlides.length;

      if (this.position === 0) {
        this.position = lastPosition;
        this.jumpTo(this.position);
      } else if (this.position === lastPosition + 1) {
        this.position = 1;
        this.jumpTo(this.position);
      }
    }

    jumpTo(position) {
      this.track.classList.add('is-jumping');
      this.track.style.transform = `translate3d(-${position * 100}%, 0, 0)`;
      void this.track.offsetWidth;
      this.track.classList.remove('is-jumping');
    }

    updateAccessibility(announce = false) {
      this.originalSlides.forEach((slide, index) => {
        const isActive = index === this.logicalIndex;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.querySelectorAll('a, button, input, select, textarea').forEach((control) => {
          if (isActive) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });
      });

      if (this.status && announce) {
        this.status.textContent = `Collezione ${this.logicalIndex + 1} di ${this.originalSlides.length}`;
      }
    }

    disableSlide(slide) {
      slide.setAttribute('aria-hidden', 'true');
      slide.querySelectorAll('a, button, input, select, textarea').forEach((control) => {
        control.setAttribute('tabindex', '-1');
      });
    }

    startAutoplay() {
      this.stopAutoplay();
      if (
        this.reducedMotion.matches ||
        document.hidden ||
        !this.inView ||
        this.isHovered ||
        this.isFocused ||
        this.isInteracting
      ) {
        return;
      }
      this.timer = window.setInterval(() => this.move(1), this.interval);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    pauseAfterInteraction() {
      this.stopAutoplay();
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.startAutoplay(), this.interval);
    }
  }

  customElements.define('moretti-cart-collection-carousel', MorettiCartCollectionCarousel);
})();
